import {
    useState,
    useCallback,
    useRef,
    useContext,
    useEffect,
    useReducer,
} from 'react'
import {
    Button,
    Typography,
    CircularProgress,
    Box,
    Paper,
    LinearProgress,
    IconButton,
    Toolbar,
    AppBar,
    FormControlLabel,
    Switch,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    DialogContentText,
    Fade,
    TextField,
    Autocomplete,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import PreviewIcon from '@mui/icons-material/Preview'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { Circle, Upload as UploadIcon } from 'lucide-react'
import * as XLSX from 'xlsx'
import Papa from 'papaparse'
import { ToastMessage } from '../../../context/ToastPopUpContext'
import responseError from '../../../context/responseError/responseError'
import {
    FormSection,
    Grid,
    SaveButton,
    UniversalAutoComplete,
    UniversalLoadingCircle,
    UniversalModal,
} from '../../UniversalComponents'
import { UniversalEditableDataTable } from '../../UniversalComponents/UniversalEditableDataTable'
import {
    addSingularManufacturerDTC,
    getManufacturerDTC,
} from '../../../apiRoutes'
import { standardColumnMapper } from '../../../utilities/functionTools'

type ManufacturerDTC = {
    dtcCode: String
    SPN: String
    FMI: String
    description: string
    severity?: number
    device?: string
}

interface Props {
    onUploadComplete: any
    manufacturerId: string
    permissionsCheck: any
    makeFormDirty: () => void
}

const MAX_FILE_SIZE = 10 * 1024 * 1024
const ALLOWED_EXTENSIONS = ['csv', 'xlsx', 'xls', 'json']

const ManufacturerDTCUploader = ({
    onUploadComplete,
    manufacturerId,
    permissionsCheck,
    makeFormDirty,
}: Props) => {
    const defaultState = {
        file: null,
        preview: [],
        isUploading: false,
        error: null,
        uploadProgress: 0,
        dtcData: [],
        data: [],
        showUploadHint: false,
        openPreview: false,
        openAddDtc: false,
        formData: {
            SPN: '',
            FMI: '',
            description: '',
            severity: '',
            device: '',
        },
        formValidation: {
            isFormDirty: false,
            isFormValid: false,
            SPNValidation: false,
            FMIValidation: false,
            descriptionValidation: false,
            severityValidation: false,
            deviceValidation: false,
        },
    }
    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)
    const {
        data,
        file,
        preview,
        isUploading,
        error,
        uploadProgress,
        dtcData,
        openPreview,
        showUploadHint,
        openAddDtc,
        formData,
        formValidation,
    } = state

    const { SPN, FMI, description, severity, device } = formData
    const {
        isFormDirty,
        isFormValid,
        SPNValidation,
        FMIValidation,
        descriptionValidation,
        severityValidation,
        deviceValidation,
    } = formValidation

    const setToastMessage = useContext(ToastMessage)

    const defaultColumns = ['SPN', 'FMI', 'description', 'severity', 'device']

    const visibleColumns = [
        {
            field: 'SPN',
            headerName: 'SPN',
            width: 200,
        },
        {
            field: 'FMI',
            headerName: 'FMI',
            width: 200,
        },
        {
            field: 'description',
            headerName: 'DESCRIPTION',
            width: 900,
        },
        {
            field: 'severity',
            headerName: 'SEVERITY',
            width: 100,
            type: 'number',
            align: 'left',
            headerAlign: 'left',
        },
        {
            field: 'device',
            headerName: 'DEVICE',
            width: 100,
        },
    ]

    const handleChange = (e: any) => {
        const { name, value } = e.target

        if (name === 'severity') {
            setState({
                formData: { ...formData, [name]: Number(value) },
            })
        } else {
            setState({
                formData: { ...formData, [name]: value },
            })
        }
    }

    const validateOnBlur = (e: any) => {
        const { name, value } = e.target

        if (value !== '') {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: false,
                    isFormDirty: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    [name + 'Validation']: true,
                    isFormDirty: true,
                },
            })
        }
    }

    const handleReset = () =>
        setState({
            formData: defaultState.formData,
            formValidation: defaultState.formValidation,
        })

    const validateDTC = (dtc: ManufacturerDTC): boolean => {
        return (
            !!dtc.FMI &&
            !!dtc.SPN &&
            dtc.description.length > 0 &&
            dtc.description.length <= 500
        )
    }

    const getData = async (active) => {
        try {
            const manufacturerDTCs = await getManufacturerDTC(
                active,
                manufacturerId
            )

            if (manufacturerDTCs && manufacturerDTCs.length !== 0) {
                const manufacturerDTCList = standardColumnMapper(
                    defaultColumns,
                    manufacturerDTCs
                )

                setState({
                    data: manufacturerDTCList,
                })
            } else {
                setState({ data: [] })
            }
        } catch (err: any) {
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    useEffect(() => {
        if (manufacturerId) {
            getData(true)
        }
    }, [])

    const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const uploadedFile = e.target.files?.[0]
            if (!uploadedFile) return

            // File size validation
            if (uploadedFile.size > MAX_FILE_SIZE) {
                setState({
                    error: 'File size exceeds 10MB limit',
                })

                return
            }

            const ext = uploadedFile.name.split('.').pop()?.toLowerCase()
            if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
                setState({
                    error: 'Invalid file type. Please use CSV, XLSX, XLS, or JSON',
                })
                return
            }

            setState({ file: uploadedFile, error: null })

            try {
                let jsonData: any[] = []

                if (ext === 'csv') {
                    const text = await uploadedFile.text()
                    const parsed = Papa.parse(text, {
                        header: true,
                        skipEmptyLines: true,
                        transform: (value) => value.trim(),
                    })
                    if (parsed.errors.length > 0) {
                        throw new Error('CSV parsing errors detected')
                    }
                    jsonData = parsed.data
                } else if (ext === 'xlsx' || ext === 'xls') {
                    const data = await uploadedFile.arrayBuffer()
                    const workbook = XLSX.read(data)
                    const worksheet = workbook.Sheets[workbook.SheetNames[0]]
                    jsonData = XLSX.utils.sheet_to_json(worksheet)
                } else if (ext === 'json') {
                    const text = await uploadedFile.text()
                    jsonData = JSON.parse(text)
                }

                const normalized: ManufacturerDTC[] = jsonData.map((item) => ({
                    dtcCode:
                        item.SPN && item.FMI != null
                            ? `${item.SPN}${item.FMI}`
                            : '',
                    SPN: item.SPN?.toString() || '',
                    FMI: item.FMI?.toString() || '',
                    description: item.Description || '',
                    severity:
                        item.Severity != null
                            ? Number(item.Severity)
                            : undefined,
                    device: item.Device || undefined,
                }))

                const valid = normalized?.filter((d) => validateDTC(d))

                if (valid.length === 0) {
                    throw new Error('No valid DTC records found in the file')
                }

                setState({
                    preview: valid,
                    dtcData: valid,
                    openPreview: true,
                })
            } catch (err: any) {
                console.error('File processing error:', err)

                setState({
                    error:
                        err.message ||
                        'Failed to parse file. Please check the format.',
                })

                setState({
                    preview: [],
                    dtcData: [],
                    file: [],
                })
            }
        },
        []
    )

    const handleFileSubmit = async () => {
        try {
            const stringifyDTCData = JSON.stringify(dtcData)
            const parsedFormData = { stringifyDTCData }

            if (parsedFormData) {
                onUploadComplete({ parsedFormData })
                setState({ showUploadHint: true })
            }
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleSubmit = async (data) => {
        try {
            const dtcData = data
            const stringifyDTCData = JSON.stringify(dtcData)
            const parsedFormData = { stringifyDTCData }

            if (parsedFormData) {
                onUploadComplete({ parsedFormData })
            }
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleSingularDTCSubmit = async () => {
        try {
            const newFormData = { ...formData, manufacturerId }

            let res: any

            {
                res = await addSingularManufacturerDTC(newFormData)
                if (res) {
                    const { data, status } = res

                    if (data && status === 200) {
                        getData(true)
                        makeFormDirty()
                        setToastMessage(
                            `DTC: ${data?.SPN} - ${data?.FMI}
                             was created successfully`,
                            'success'
                        )

                        setState({
                            openAddDtc: false,
                            formData: defaultState.formData,
                        })
                    } else {
                        setToastMessage(
                            ` ERROR: ${res?.response?.data}`,
                            'error'
                        )
                    }
                }
            }
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    useEffect(() => {
        if (
            isFormDirty &&
            !SPNValidation &&
            !FMIValidation &&
            !descriptionValidation &&
            !severityValidation &&
            !deviceValidation &&
            SPN &&
            FMI &&
            description &&
            severity &&
            device
        ) {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: true,
                },
            })
        } else {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: false,
                },
            })
        }
    }, [
        isFormDirty,
        SPNValidation,
        FMIValidation,
        descriptionValidation,
        severityValidation,
        deviceValidation,
        SPN,
        FMI,
        description,
        severity,
        device,
    ])

    const severityOptions = ['1', '2', '3']

    return (
        <Grid item xs={12} md={12} lg={12}>
            <Grid item xs={12} md={12} lg={12}>
                <Box display="flex" flexDirection="column" gap={2}>
                    <Toolbar
                        sx={{
                            bgcolor: 'background.paper',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            borderRadius: '8px',
                            py: 1.5,
                            px: 3,
                            width: '100%',
                        }}
                    >
                        <Grid
                            container
                            alignItems="center"
                            spacing={1.5}
                            sx={{ justifyContent: 'space-between' }}
                        >
                            <Grid item xs={12} md={12} lg={12}>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        component="div"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'primary.main',
                                            display: {
                                                xs: 'none',
                                                sm: 'block',
                                            },
                                        }}
                                    >
                                        Manufacturer DTC File Uploader
                                    </Typography>
                                    {showUploadHint && (
                                        <Fade in={showUploadHint}>
                                            <Typography
                                                variant="body1"
                                                color="info"
                                                sx={{
                                                    mt: 0.5,
                                                    display: {
                                                        xs: 'none',
                                                        sm: 'block',
                                                    },
                                                }}
                                            >
                                                Save the Manufacturer Form to
                                                submit changes.
                                            </Typography>
                                        </Fade>
                                    )}
                                </Box>
                            </Grid>

                            {/* File Info and Progress */}
                            <Grid item xs={12} md={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    {file && (
                                        <Fade in={!!file}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    mb: isUploading ? 0.5 : 0,
                                                }}
                                            >
                                                Selected: {file.name} (
                                                {(
                                                    file.size /
                                                    1024 /
                                                    1024
                                                ).toFixed(2)}{' '}
                                                MB)
                                            </Typography>
                                        </Fade>
                                    )}
                                    {isUploading && (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                maxWidth: 180,
                                                mx: 'auto',
                                            }}
                                        >
                                            <LinearProgress
                                                variant="determinate"
                                                value={uploadProgress}
                                                sx={{
                                                    height: 5,
                                                    borderRadius: 3,
                                                    bgcolor: 'grey.200',
                                                    '& .MuiLinearProgress-bar':
                                                        {
                                                            bgcolor:
                                                                'primary.main',
                                                            transition:
                                                                'width 0.3s ease-in-out',
                                                        },
                                                }}
                                            />
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ mt: 0.5 }}
                                            >
                                                Uploading: {uploadProgress}%
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>

                            {/* Action Buttons */}
                            <Grid
                                item
                                xs={12}
                                md={8}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: 1,
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<UploadIcon />}
                                    disabled={isUploading}
                                    sx={{
                                        borderRadius: '20px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        px: 2.5,
                                        py: 0.75,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            borderColor: 'primary.main',
                                        },
                                    }}
                                >
                                    Select File
                                    <input
                                        type="file"
                                        accept=".csv,.xlsx,.xls,.json"
                                        hidden
                                        onChange={handleFileChange}
                                    />
                                </Button>

                                <Button
                                    variant="outlined"
                                    startIcon={<PreviewIcon />}
                                    disabled={
                                        !file ||
                                        preview.length === 0 ||
                                        isUploading
                                    }
                                    onClick={() =>
                                        setState({ openPreview: true })
                                    }
                                    sx={{
                                        borderRadius: '20px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        px: 2.5,
                                        py: 0.75,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            borderColor: 'primary.main',
                                        },
                                    }}
                                >
                                    Preview
                                </Button>

                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<RestartAltIcon />}
                                    disabled={
                                        !file ||
                                        preview.length === 0 ||
                                        isUploading
                                    }
                                    onClick={() =>
                                        setState({
                                            file: null,
                                            preview: [],
                                            isUploading: false,
                                            error: null,
                                            uploadProgress: 0,
                                            dtcData: [],

                                            showUploadHint: false,
                                            openPreview: false,
                                        })
                                    }
                                    sx={{
                                        borderRadius: '20px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        px: 2.5,
                                        py: 0.75,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: 'error.dark',
                                            color: 'black',
                                            borderColor: 'black',
                                            boxShadow:
                                                '0 2px 8px rgba(0, 0, 0, 0.2)',
                                        },
                                    }}
                                >
                                    Reset
                                </Button>

                                <Button
                                    variant="contained"
                                    startIcon={
                                        isUploading ? (
                                            <CircularProgress
                                                size={16}
                                                color="inherit"
                                            />
                                        ) : (
                                            <CloudUploadIcon />
                                        )
                                    }
                                    disabled={
                                        !file ||
                                        preview.length === 0 ||
                                        isUploading
                                    }
                                    onClick={handleFileSubmit}
                                    sx={{
                                        borderRadius: '20px',
                                        textTransform: 'none',
                                        width: '30%',
                                        fontWeight: 500,
                                        px: 2.5,
                                        py: 0.75,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: 'primary.dark',
                                            color: 'black',
                                            borderColor: 'black',
                                            boxShadow:
                                                '0 2px 8px rgba(0, 0, 0, 0.2)',
                                        },
                                    }}
                                >
                                    {isUploading ? 'Uploading...' : 'Upload'}
                                </Button>
                            </Grid>
                        </Grid>
                    </Toolbar>

                    <Dialog
                        fullWidth={true}
                        maxWidth="lg"
                        open={openPreview}
                        onClose={() => setState({ openPreview: false })}
                    >
                        <Grid item xs={12} sx={{ padding: 2 }}>
                            {preview?.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle1">
                                        Preview ({preview?.length} valid
                                        records)
                                    </Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1,
                                            maxHeight: 500,
                                            overflow: 'auto',
                                            bgcolor: 'background.default',
                                        }}
                                    >
                                        <pre
                                            style={{
                                                fontSize: 12,
                                                margin: 0,
                                            }}
                                        >
                                            <Box sx={{ minWidth: 600 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        bgcolor:
                                                            'background.paper',
                                                        p: 1,
                                                        fontWeight: 'bold',
                                                        borderBottom: 1,
                                                        borderColor: 'divider',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            flex: 0.125,
                                                            px: 1,
                                                        }}
                                                    >
                                                        SPN
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            flex: 0.125,
                                                            px: 1,
                                                        }}
                                                    >
                                                        FMI
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            flex: 0.9,
                                                            px: 1,
                                                        }}
                                                    >
                                                        Description
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            flex: 0.125,
                                                            px: 1,
                                                        }}
                                                    >
                                                        Severity
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            flex: 0.125,
                                                            px: 1,
                                                        }}
                                                    >
                                                        Device
                                                    </Box>
                                                </Box>

                                                {preview?.map((item, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            display: 'flex',
                                                            p: 1,
                                                            borderBottom: 1,
                                                            borderColor:
                                                                'divider',
                                                            bgcolor:
                                                                'background.paper',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                flex: 0.125,
                                                                px: 1,
                                                                fontFamily:
                                                                    'monospace',
                                                            }}
                                                        >
                                                            {item?.SPN}
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                flex: 0.125,
                                                                px: 1,
                                                                fontFamily:
                                                                    'monospace',
                                                            }}
                                                        >
                                                            {item?.FMI}
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                flex: 0.9,
                                                                px: 1,
                                                                maxLines: 2,
                                                            }}
                                                        >
                                                            {item?.description}
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                flex: 0.125,
                                                                px: 1,
                                                            }}
                                                        >
                                                            {item?.severity ??
                                                                '-'}
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                flex: 0.125,
                                                                px: 1,
                                                            }}
                                                        >
                                                            {item?.device ??
                                                                '-'}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </pre>
                                    </Paper>
                                </Box>
                            )}
                        </Grid>
                        <DialogActions>
                            <Button
                                onClick={() => setState({ openPreview: false })}
                            >
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
                <UniversalModal
                    open={openAddDtc}
                    title="Add Single DTC to Manufacturer"
                    onClose={() =>
                        setState({
                            openAddDtc: false,
                        })
                    }
                >
                    <Grid container spacing={2} sx={{ marginTop: 20 }}>
                        <Grid
                            item
                            xs={12}
                            md={12}
                            lg={12}
                            sx={{ marginBottom: 10 }}
                        >
                            <Typography
                                variant="h4"
                                align="center"
                                color="primary"
                            >
                                Please fill out all fields to submit a new DTC
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                required={true}
                                label="SPN"
                                name="SPN"
                                type="number"
                                onChange={handleChange}
                                fullWidth
                                value={SPN}
                                onBlur={validateOnBlur}
                                error={SPNValidation && !SPN}
                                helperText={
                                    SPNValidation && !SPN
                                        ? 'Please enter a SPN.'
                                        : ''
                                }
                                disabled={permissionsCheck}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                required={true}
                                label="FMI"
                                name="FMI"
                                type="number"
                                onChange={handleChange}
                                fullWidth
                                value={FMI}
                                onBlur={validateOnBlur}
                                error={FMIValidation && !FMI}
                                helperText={
                                    FMIValidation && !FMI
                                        ? 'Please enter a FMI.'
                                        : ''
                                }
                                disabled={permissionsCheck}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                required={true}
                                label="Description"
                                name="description"
                                onChange={handleChange}
                                fullWidth
                                value={description}
                                disabled={permissionsCheck}
                                onBlur={validateOnBlur}
                                error={descriptionValidation && !description}
                                helperText={
                                    descriptionValidation && !description
                                        ? 'Please enter a Description.'
                                        : ''
                                }
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Autocomplete
                                options={severityOptions}
                                getOptionLabel={(option) => String(option)}
                                disabled={permissionsCheck}
                                onBlur={validateOnBlur}
                                value={severity}
                                onChange={(e, newValue) => {
                                    const data = {
                                        target: {
                                            name: 'severity',
                                            value: newValue,
                                        },
                                    }

                                    handleChange(data)
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Severity"
                                        required={true}
                                        name="severity"
                                        error={severityValidation && !severity}
                                        helperText={
                                            severityValidation && !severity
                                                ? 'Please assign a Severity number.'
                                                : ''
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                required={true}
                                label="Device"
                                name="device"
                                onChange={handleChange}
                                fullWidth
                                value={device}
                                onBlur={validateOnBlur}
                                error={deviceValidation && !device}
                                helperText={
                                    deviceValidation && !device
                                        ? 'Please select a Device.'
                                        : ''
                                }
                                disabled={permissionsCheck}
                            />
                        </Grid>

                        <Grid xs={12} md={12} lg={12}>
                            <SaveButton
                                handleSubmit={handleSingularDTCSubmit}
                                handleReset={handleReset}
                                disabledSave={!isFormValid || permissionsCheck}
                                disabledReset={!isFormDirty || permissionsCheck}
                            />
                        </Grid>
                    </Grid>
                </UniversalModal>
            </Grid>

            <FormSection title="Manufacturer DTC Table">
                <Grid item xs={12}>
                    <UniversalLoadingCircle
                        data={data}
                        reloadFunction={() => getData(true)}
                        customTimeoutMessage={'No Manufacturer DTCs found'}
                    >
                        <UniversalEditableDataTable
                            data={data}
                            visibleColumns={visibleColumns}
                            defaultColumns={defaultColumns}
                            customButtonAddShow={true}
                            customButtonAdd={() =>
                                setState({ openAddDtc: true })
                            }
                            customButtonAddName={'DTC'}
                            columnsAvailable={[]}
                            updatedRow={(data) => {
                                handleSubmit(data)
                                setState({ dtcData: data })
                            }}
                        />
                    </UniversalLoadingCircle>
                </Grid>
            </FormSection>
        </Grid>
    )
}

export default ManufacturerDTCUploader
